import { Paper, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import { formatShortDate } from "components/Admin/Organizations/OrganizationDetail/OrganizationClientBrain/clientBrainUtils";
import UserContextStore from "components/Admin/Organizations/OrganizationDetail/OrganizationClientBrain/UserContextStore";
import UserContextFieldsEditor from "components/KBo/ClientBrain/UserContextFieldsEditor";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import { observer } from "mobx-react";
import React from "react";
import ClientBrainService from "services/ClientBrainService";
import { useStore } from "utils/useStore";

/**
 * Self-service "User settings" tab under KBo Settings. Lets the current user edit the same
 * per-user Client Brain context (Job role, Goals, Responsibilities, Success Metrics, Current
 * Concerns) that admins edit for any user from the Client Brain "User Profiles" section — both
 * surfaces read/write the same record. The KBo Flow subscription lives on "My subscriptions".
 */
const UserSettingsPage = observer(function UserSettingsPage() {
    const theme = useTheme();
    const messageStore = useStore(MessageStore);
    const clientBrainService = React.useMemo(
        () => new ClientBrainService(),
        [],
    );

    const [loading, setLoading] = React.useState(true);
    const [contextStore, setContextStore] = React.useState<UserContextStore>();

    React.useEffect(() => {
        let cancelled = false;

        clientBrainService
            .getMyUserContext()
            .then((profile) => {
                if (cancelled) return;
                setContextStore(
                    new UserContextStore(
                        profile,
                        messageStore,
                        (_userId, payload) =>
                            clientBrainService.updateMyUserContext(payload),
                    ),
                );
            })
            .catch((error) => {
                if (!cancelled) {
                    messageStore.logError(
                        error instanceof Error
                            ? error.message
                            : "User settings could not be loaded.",
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Stack rowGap={2} padding={3} width="100%">
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                columnGap={2}
            >
                <Stack rowGap={0.5}>
                    <Typography variant="h5" fontWeight={600}>
                        User settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage the context KBo uses to decide what to research
                        and how to frame findings for you. Update it any time.
                    </Typography>
                </Stack>
                {contextStore &&
                    (contextStore.isEditing ? (
                        <Stack direction="row" columnGap={1} flexShrink={0}>
                            <AcxButton
                                color="white"
                                onClick={() => contextStore.cancelEdit()}
                                disabled={contextStore.isSaving}
                            >
                                Cancel
                            </AcxButton>
                            <AcxButton
                                color="primary"
                                onClick={() => contextStore.save()}
                                loading={contextStore.isSaving}
                            >
                                Save changes
                            </AcxButton>
                        </Stack>
                    ) : (
                        <AcxButton
                            color="primary"
                            variant="text"
                            onClick={() => contextStore.startEdit()}
                        >
                            Edit context
                        </AcxButton>
                    ))}
            </Stack>

            {loading || !contextStore ? (
                <Skeleton variant="rounded" height={480} />
            ) : (
                <Stack id="kbo-user-context" rowGap={1}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        columnGap={2}
                    >
                        <Typography
                            variant="overline"
                            sx={{
                                color: theme.palette.gray[400],
                                fontWeight: 600,
                                letterSpacing: "0.06em",
                            }}
                        >
                            Your context
                        </Typography>
                        {(contextStore.context.modifiedBy ||
                            contextStore.context.modifiedOn) && (
                            <Typography
                                sx={{
                                    fontSize: "12px",
                                    color: theme.palette.gray[400],
                                }}
                            >
                                Last updated by{" "}
                                {contextStore.context.modifiedBy} ·{" "}
                                {formatShortDate(
                                    contextStore.context.modifiedOn,
                                )}
                            </Typography>
                        )}
                    </Stack>
                    <Paper
                        id="kbo-user-context"
                        variant="outlined"
                        sx={{
                            padding: theme.spacing(2),
                            borderRadius: "8px",
                        }}
                    >
                        <UserContextFieldsEditor
                            ctx={
                                contextStore.isEditing
                                    ? contextStore.draft
                                    : contextStore.context
                            }
                            isEditing={contextStore.isEditing}
                            onSetRole={(value) => contextStore.setRole(value)}
                            onSetListFieldText={(field, text) =>
                                contextStore.setListFieldText(field, text)
                            }
                            keyPrefix={contextStore.userId}
                        />
                    </Paper>
                </Stack>
            )}
        </Stack>
    );
});

export default UserSettingsPage;
